import React, {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import classNames from 'classnames';
import photoPlaceholder from '../../assets/images/photo-placeholder.svg';
import videoPlaceholder from '../../assets/images/video-placeholder.svg';

const Media = forwardRef((
  {
    src,
    mediaType,
    videoSrc,
    descriptionEnabled = false,
  },
  ref,
) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef(null);
  const playTimeoutRef = useRef(null);
  const pauseTimeoutRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    if (mediaType === 'video/mp4' && videoRef.current && videoSrc && !descriptionEnabled) {
      // Clear any pending pause
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }

      // Clear any pending play and start new one
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
      }

      setIsHovering(true);
      playTimeoutRef.current = setTimeout(() => {
        if (videoRef.current && !videoRef.current.paused) {
          // Video is already playing, don't interrupt
          return;
        }
        videoRef.current.play().catch((error) => {
          // Ignore AbortError - it's expected when play() is interrupted by pause()
          if (error.name !== 'AbortError') {
            console.error('Video play error:', error);
          }
        });
      }, 100); // Small delay to prevent rapid play/pause cycles
    }
  }, [mediaType, videoSrc, descriptionEnabled]);

  const handleMouseLeave = useCallback(() => {
    if (mediaType === 'video/mp4' && videoRef.current && !descriptionEnabled) {
      // Clear any pending play
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
        playTimeoutRef.current = null;
      }

      setIsHovering(false);
      pauseTimeoutRef.current = setTimeout(() => {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }, 50); // Small delay to prevent rapid play/pause cycles
    }
  }, [mediaType, descriptionEnabled]);

  useImperativeHandle(ref, () => ({
    handleMouseEnter,
    handleMouseLeave,
  }));

  // Cleanup timeouts on unmount
  useEffect(() => () => {
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
    }
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
  }, []);

  if (imgError) {
    return (
      <div className="imagePlaceholder">
        <img src={mediaType === 'video/mp4' ? videoPlaceholder : photoPlaceholder} alt="placeholder" />
      </div>
    );
  }

  const isVideo = mediaType === 'video/mp4' && videoSrc && !descriptionEnabled;

  return (
    <>
      {!imgLoaded && <div className="glow" />}

      {isVideo ? (
        <div className="videoContainer">
          <img
            className="videoThumbnail"
            src={src}
            alt=""
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
          />
          <video
            ref={videoRef}
            className={classNames('videoElement', { visible: isHovering })}
            src={videoSrc}
            muted
            loop
            playsInline
            preload="metadata"
            onError={() => {
              setImgError(true);
            }}
          />
        </div>
      ) : (
        <img
          className="image"
          src={src}
          alt=""
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
        />
      )}
    </>
  );
});

export default Media;

